<template>
  <template v-if="isMobile && !isMobileInline">
    <overlay
      key="quick-add-to-cart-modal"
      block-class="quick-add-to-cart"
      direction="up"
      mobile-drag-close
      namespace="quick-add-to-cart-modal"
      :show-close="false"
      type="shelf-modal"
    >
      <template
        v-if="!itemAdded && !showBackInStockModal"
        #title
      >
        <h2 class="quick-add-to-cart__header text-body-2xs-regular">
          {{ getSelectOptionText() }}
        </h2>
      </template>

      <template #body>
        <klaviyo-bis-drawer
          v-if="showBackInStockModal"
          :cta-text="$string('product.back_in_stock.ok_cta')"
          :display-text="$string('product.back_in_stock.notify_email')"
          :product="product"
          :size-value="selectedSize.value || ''"
          :variant="variant"
          :namespace="`${bisSettings.namespace}-quick-atc-mobile`"
          @close-drawer="showBackInStockModal = false"
          @form-submitted="handleKlaviyoFormSubmit"
        />

        <template v-else>
          <quick-add-success
            v-if="itemAdded"
            :product="stateProduct"
            :size="selectedSize.value"
            type="inline"
            @success-message-close="closeQuickAdd"
          />

          <template v-else>
            <div class="quick-add-to-cart__closer-wrapper">
              <div class="quick-add-to-cart__closer" />
            </div>

            <div
              class="quick-add-to-cart__grid"
              :class="[
                {
                  'quick-add-to-cart__grid--one-size': isOneSize,
                  [$class('disabled')]: isShippingUnavailable(),
                }
              ]"
            >
              <template
                v-for="(size) of sizes"
                :key="`mobile-${size.id}`"
              >
                <div
                  v-if="size && size.id"
                  class="quick-add-to-cart__grid-item-wrapper col"
                >
                  <button
                    class="quick-add-to-cart__grid-item text-body-2xs-regular"
                    :class="{
                      [$class('active')]: size.label == selectedSize.value,
                      'not-available': !size.available,
                    }"
                    :aria-label="$string(
                      'product.quick_add.size_add_to_cart',
                      {
                        replace: { size: getOptionLabel(size.label) },
                      }
                    )"
                    modifiers="secondary center"
                    type="button"
                    @click.prevent="handleClickOnSize(size)"
                  >
                    <span
                      class="
                        quick-add-to-cart__grid-item-size-label
                        text-body-2xs-regular
                      "
                      v-text="getOptionLabel(size.label)"
                    />

                    <span
                      v-if="getStatusClass(size) === 'pre-order'"
                      class="text-body-2xs-regular"
                      :class="getStatusClass(size)"
                      v-html="getMobileSizeMessage(size)"
                    />

                    <span
                      v-else-if="getStatusClass(size) === 'low-stock'"
                      class="text-body-2xs-regular"
                      :class="getStatusClass(size)"
                      v-html="getMobileSizeMessage(size)"
                    />

                    <span
                      v-else-if="getStatusClass(size) === 'status-notify'"
                      class="text-body-2xs-regular"
                      :class="getStatusClass(size)"
                    >
                      <icon-envelope />
                      <span v-text="getMobileSizeMessage(size)" />
                    </span>
                  </button>
                </div>
              </template>
            </div>

            <div
              v-if="!isOneSize"
              class="quick-add-to-cart__size-guide-wrapper"
            >
              <div
                v-if="isShippingUnavailable()"
                class="product-form__shipping-unavailable"
                v-text="$string('product.form.shipping_unavailable')"
              />

              <btn
                class="quick-add-to-cart__size-guide-button"
                :label="$string('product.cta.size_guide')"
                modifiers="text center"
                type="button"
                @click="openSizeGuide"
              />
            </div>
          </template>
        </template>
      </template>
    </overlay>
  </template>

  <template v-else>
    <div
      class="quick-add-to-cart"
      :class="{
        'mobile-inline': isMobileInline,
        'bis-active': showBackInStockModal,
        [$class('disabled')]: isShippingUnavailable(),
      }"
      :style="{ '--size-count-per-row': `${sizeCountPerRow}` }"
    >
      <div
        v-if="showBackInStockModal &&
          (isMobile ? isMobileInline : !bisSettings.showFormOnly)
        "
        class="quick-add-to-cart__bis"
        :class="{
          'btn--left': bisSettings.showCloseIcon
        }"
      >
        <klaviyo-bis-drawer
          v-if="isMobile && isMobileInline"
          :cta-text="$string('product.back_in_stock.ok_cta')"
          :display-text="$string('product.back_in_stock.notify_email')"
          :product="product"
          :size-value="selectedSize.value || ''"
          :variant="variant"
          :namespace="`${bisSettings.namespace}-quick-atc-mobile`"
          @close-drawer="showBackInStockModal = false"
          @form-submitted="handleKlaviyoFormSubmit"
        />

        <klaviyo-bis-drawer
          v-if="!isMobile && !bisSettings.showFormOnly"
          :cta-text="bisSettings.ctaText || $string('product.back_in_stock.ok_cta')"
          :display-text="bisSettings.displayText || $string('product.back_in_stock.notify_email')"
          :footer-text="bisSettings.footerText"
          :marketing-text="bisSettings.marketingText"
          :product="product"
          :size-value="selectedSize.value || ''"
          :show-close-icon="bisSettings.showCloseIcon"
          :show-close-button="bisSettings.showCloseButton"
          :variant="variant"
          :namespace="`${bisSettings.namespace}-quick-atc-desktop`"
          @close-drawer="showBackInStockModal = false"
          @form-submitted="handleKlaviyoFormSubmit"
        />
      </div>

      <div v-else>
        <div
          v-if="showProductDetails"
          class="quick-add-to-cart__product-info"
        >
          <div class="quick-add-to-cart__product">
            <h2
              :id="`product-title-${product.handle}`"
              class="
                quick-add-to-cart__title
                text-body-2xs-regular
              "
              :class="{'font-family-freight large-font': productCardLocation?.key === 'plpHotspot'}"
              v-text="product.title"
            />

            <product-prices
              class="quick-add-to-cart__prices"
              :available="product?.available"
              :compare-at-price="product?.compareAtPrice?.amount"
              :price="product?.price?.amount"
              :varies="product?.price?.varies"
              class-price="text-body-2xs-regular"
              class-compare-at-price="text-body-2xs-regular"
            />

            <p
              class="text-body-2xs-regular"
              v-text="viewedProductSwatch"
            />
          </div>

          <div class="quick-add-to-cart__bookmark_icon">
            <wishlist-button
              v-show="renderWishlistBtn"
              class="quick-add-to-cart__wishlist"
              :class="{ 'is-wishlist-page': isWishlistPage }"
              :product="product || stateProduct"
              template="hotspot"
              :variant="(product || stateProduct).selectedOrFirstAvailableVariant"
            />
          </div>
        </div>

        <div
          v-if="sizeGuidePosition === 'top'"
          class="quick-add-to-cart__size-guide-wrapper top"
        >
          <div class="quick-add-to-cart__size">
            <span
              class="text-body-2xs-regular"
              v-text="getOptionDisplayName(selectedSize.value)"
            />

            <span
              v-if="scarcityMessage"
              class="text-body-2xs-regular"
              v-text="scarcityMessage"
            />
          </div>

          <btn
            class="quick-add-to-cart__size-guide-button"
            :label="$string('product.cta.size_guide')"
            :style="{ 'visibility': isOneSize ? 'hidden' : '' }"
            modifiers="text center"
            type="button"
            @click="openSizeGuide"
          />
        </div>

        <div
          class="quick-add-to-cart__grid grid"
          :class="{ 'quick-add-to-cart__grid--one-size': isOneSize }"
        >
          <template
            v-for="(size) of sizes"
            :key="`top-${size.id}`"
          >
            <btn
              v-if="size && size.id"
              :id="size.id"
              ref="sizeButtons"
              class="quick-add-to-cart__grid-item col l1"
              :class="{
                [$class('active')]: size.label == selectedSize.value,
                'not-available': !size.available,
              }"
              :aria-label="$string(
                'product.quick_add.size_add_to_cart',
                {
                  replace: { size: getOptionLabel(size.label) },
                }
              )"
              :label="getOptionLabel(size.label)"
              modifiers="secondary center"
              type="button"
              :value="getOptionLabel(size.label)"
              :available="size.available"
              @click.prevent="handleClickOnSize(size)"
            />
          </template>
        </div>

        <div
          v-if="sizeGuidePosition === 'bottom'"
          class="quick-add-to-cart__size-guide-wrapper bottom"
          :style="{
            'visibility': isOneSize ? 'hidden' : '',
            'justify-content': isPLP && isShippingUnavailable() ? 'center' : ''
          }"
        >
          <btn
            :label="$string('product.cta.size_guide')"
            class="quick-add-to-cart__size-guide-button"
            class-label="text-body-2xs-regular"
            modifiers="text center"
            type="button"
            @click="openSizeGuide"
          />
        </div>

        <div
          v-if="isShippingUnavailable() &&
            (isPLP ||
              productCardLocation.key === 'sectionHotspot' ||
              productCardLocation.key === 'carousel' ||
              productCardLocation === 'productTray')
          "
          class="product-form__shipping-unavailable"
          v-text="$string('product.form.shipping_unavailable')"
        />

        <div
          v-if="showConfirmationButton || showViewProductButton"
          class="quick-add-to-cart__buttons-wrapper"
        >
          <div
            v-if="isShippingUnavailable() && isPDP"
            class="product-form__shipping-unavailable"
            v-text="$string('product.form.shipping_unavailable')"
          />

          <klaviyo-bis-form
            v-if="showBISForm && !isShippingUnavailable()"
            class="quick-add-to-cart__klaviyo-form"
            :namespace="`${bisSettings.namespace}-quick-atc-desktop`"
            :cta-text="bisSettings.ctaText"
            :display-text="bisSettings.displayText"
            :product="product"
            :variant="variant"
            @form-submitted="handleKlaviyoFormSubmit"
          />

          <btn
            v-else-if="showConfirmationButton && !isShippingUnavailable()"
            class="quick-add-to-cart__confirm-button"
            :disabled="addToCartDisabled"
            :label="addToCartText"
            modifiers="primary block center"
            :show-state-icon="state === 'adding'"
            type="button"
            :data-atc-source="productCardLocation?.key"
            @click="addItemToCart"
          >
            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>

          <div
            v-if="showViewProductButton"
            class="quick-add-to-cart__extra-details"
          >
            <p
              v-if="preorderMessage"
              class="quick-add-to-cart__preorder text-body-2xs-regular"
              v-html="preorderMessage"
            />

            <div class="text-center">
              <btn
                v-if="showViewProductButton"
                class="quick-add-to-cart__view-product-button"
                :label="$string('product.cta.view_product_details')"
                type="button"
                modifiers="text center"
                :url="product?.url"
                @click="handleHrefClick"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-if="isMobile && isMobileInline">
      <teleport to="body">
        <!-- TODO: Remove this overlay, or site-header overlay with same namespace -->
        <overlay
          key="product-form-success-mobile"
          block-class="quick-add-success-modal"
          namespace="shop-the-look-success-modal"
          type="shelf-modal"
          show-overlay
          mobile-drag-close
          :show-close="false"
          direction="up"
        >
          <template #body>
            <quick-add-success
              v-if="itemAdded"
              :product="stateProduct"
              :size="selectedSize.value"
              :show-upsell="shouldShowUpsell"
              type="inline"
              @success-message-close="closeSuccessMobile"
            />
          </template>
        </overlay>
      </teleport>
    </template>
  </template>
</template>

<script>

/**
 * Vue: Quick add to cart (quick-add-to-cart)
 * -----------------------------------------------------------------------------
 * Quick add to cart component.
 *
 * @param {Array} [product] - Product attached.
 * @param {Array} [properties] - Optional line item properties to include.
 * @param {Number} [numberOfSizesPerRow] - Number of size buttons per row
 * (when not mobile shelf)
 * @param {Boolean} [showConfirmationButton] - Adds confirm button to size
 * selection
 * @param {Boolean} [showViewProductButton] - Adds view product button to size
 * selection
 * @param {Boolean} [isMobileInline] - Instead of showing mobile add to cart as
 * shelf overlay, the content is being shown wherever the section has been added
 * (useful for sections like Lookbook, Shop the Look)
 * @param {Boolean} [showProductDetails] - Shows product details above
 * size selection.
 * @param {String} [placement] - Placement of quick add to cart to change
 * success behaviour.
 * size selection
 * @param {Object} [bisSettings] - Settings for back in stock display in klaviyo
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import KlaviyoBisDrawer from '~async/klaviyo-bis/klaviyo-bis-drawer'
import KlaviyoBisForm from '~async/klaviyo-bis/klaviyo-bis-form'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import QuickAddSuccess from './quick-add-success'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import IconLoading from '~icons/general/loading.svg'
import IconEnvelope from '~icons/general/envelope.svg'

export default {
  name: 'QuickAddToCart',

  components: {
    Btn,
    IconLoading,
    IconEnvelope,
    Overlay,
    KlaviyoBisDrawer,
    KlaviyoBisForm,
    ProductPrices,
    QuickAddSuccess,
    WishlistButton,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },
    properties: {
      type: Object,
      default: () => ({}),
    },
    numberOfSizesPerRow: {
      type: [Boolean, Number],
      default: false,
    },
    showBackInStock: {
      type: Boolean,
      default: false,
    },
    showConfirmationButton: {
      type: Boolean,
      default: false,
    },
    showViewProductButton: {
      type: Boolean,
      default: false,
    },
    isMobileInline: {
      type: Boolean,
      default: false,
    },
    showProductDetails: {
      type: Boolean,
      default: false,
    },
    isStlType: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: '',
    },
    isPdpStl: {
      type: Boolean,
      default: false,
    },
    isWishlistPage: {
      type: Boolean,
      default: false,
    },
    sizeGuidePosition: {
      type: String,
      default: 'bottom',
    },
    productCardLocation: {
      type: Object,
      default: null,
    },
    finalSale: {
      type: Boolean,
      default: false,
    },
    showCartDrawer: {
      type: Boolean,
      default: false,
    },

    /* Desktop modifications for bis drawer */
    bisSettings: {
      type: Object,
      default: () => ({
        ctaText: '',
        displayText: '',
        footerText: '',
        marketingText: '',
        namespace: '',
        showCloseButton: '',
        showCloseIcon: '',
        showFormOnly: '',
      }),
    },
  },

  emits: {
    'bis-active': (payload) => typeof payload === 'boolean',
    'wishlist-add-to-cart': () => true,
    'form-submitted': () => true,
  },

  data() {
    return {
      cnvsGlobal: true,
      isOneSize: false,
      itemAdded: false,
      lineItemProperties: {},
      selectedSize: {},
      sizeCountPerRow: 5,
      sizes: [],
      showBackInStockModal: this.showBackInStock,
      state: 'ready',
      isPLP: this.$variable('template.name') === 'collection',
      isPDP: this.$variable('template.name') === 'product',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      stateProduct: (state) => state.quickAddToCart.product,
      showUpdatedSuccess: (state) => state.quickAddToCart.isUpdatedSuccess,
    }),

    /**
     * Compute whether to disable add to cart button.
     * @returns {Boolean}
     */
    addToCartDisabled() {
      return (
        !this.selectedSize.id ||
        !this.selectedSize.available ||
        this.selectedSize.available === 'false'
      )
    },

    /**
     * Compute whether to disable bis form.
     * @returns {Boolean}
     */
    showBISForm() {
      return (!this.isMobile && this.bisSettings.showFormOnly) &&
        (!this.selectedSize.available || this.selectedSize.available === 'false') &&
        Object.keys(this.selectedSize).length
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.variant === false && this.state === 'ready') {
        if (
          (this.product && Array.isArray(this.product.variants)) &&
          this.product.preorder.skus !== null) {
          const soldOutCount = this.product.variants.filter(variant => !variant.available).length
          const preorderCount = this.product.preorder.skus.length
          if ((soldOutCount + preorderCount) === this.product.variants.length) {
            return this.$string('product.cta.preorder')
          }
        }
      }

      if (
        this.selectedSize.id &&
        (!this.selectedSize.available ||
          this.selectedSize.available === 'false')
      ) {
        return this.$string('product.cta.sold_out')
      }

      if (this.preorderVariant || this.preorderVariants) {
        return this.$string('product.cta.preorder')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute preorder line item properties.
     * @returns {Null|Object}
     */
    preorderLineItemProperties() {
      const product = this.product || this.stateProduct
      const date = this.$formatPreorderDate({
        date: product?.preorder?.date,
        dateFormat: 'preorder_cart_html',
      })
      const preorderBadge = this.$string('product.preorder.cart', { replace: { date } })

      return {
        preorderBadge,
      }
    },

    /**
     * Compute preorder message.
     * - If there should be one.
     * @returns {String}
     */
    preorderMessage() {
      const product = this.product || this.stateProduct
      if (!product?.preorder?.date || !product?.preorder?.skus?.length) {
        return ''
      }

      if (this.preorderVariant || this.preorderVariants) {
        const date = this.$formatPreorderDate({
          date: product?.preorder?.date,
          dateFormat: 'preorder_pdp_html',
        })
        return this.$string('product.preorder.form', { replace: { date } })
      }

      return ''
    },

    /**
     * Compute if selected variant is a preorder.
     * @returns {Boolean}
     */
    preorderVariant() {
      const product = this.product || this.stateProduct
      return product?.preorder?.skus?.includes(this.variant?.sku)
    },

    /**
     * Compute if all variants are preorders.
     * @returns {Boolean}
     */
    preorderVariants() {
      const product = this.product || this.stateProduct
      return product?.variants?.every((variant) => {
        return product?.preorder?.skus?.includes(variant.sku)
      })
    },

    /**
     * Compute variant from selected size.
     * - Used for preorder logic.
     * @returns {Boolean|Object}
     */
    variant() {
      return this.product?.variants?.find((variant) => {
        return Number(this.selectedSize.id) === variant.id
      }) || false
    },

    /**
     * Compute scarcity message.
     * @returns {String|Boolean}
     */
    scarcityMessage() {
      if (!this.variant || !this.variant?.available || this.product.giftCard) {
        return false
      }

      if (this.variant?.inventory <= this.$variable('settings.lowStockThreshold')) {
        return `(${this.$string('product.form.low_stock')})`
      }

      return false
    },

    /**
     * Check upsell eligible page
     */
    shouldShowUpsell() {
      return this.productCardLocation?.key === 'productTray' ||
        this.productCardLocation?.key === 'carousel' ||
        this.productCardLocation?.key === 'productListing'
    },

    /**
     * Compute viewed product swatch.
     * @returns {String}
     */
    viewedProductSwatch() {
      if (!this.product || !this.$variable('settings.product.productSwatchOptionNames')) {
        return ''
      }

      const variant = this.product?.selectedOrFirstAvailableVariant
      const swatchOption = variant?.selectedOptions?.find((option) => {
        return this.$variable('settings.product.productSwatchOptionNames')?.includes(option.name?.toLowerCase())
      })

      if (!swatchOption) {
        return ''
      }

      return this.$string(`product.cta.${swatchOption.name.toLowerCase()}_label`, {
        replace: {
          [swatchOption.name.toLowerCase()]: swatchOption.value || '',
        },
      })
    },

    /**
     * Hides wishlist button from CA store
     */
    renderWishlistBtn() {
      return window.cnvs?.store.permanentDomain !== '//veronica-beard-canada.myshopify.com/'
    },
  },

  watch: {

    /**
     * Watch product object update
     */
    product() {
      this.getProductSizes()
      this.getSizesPerRow()
      this.autoFocusFirstSize()
    },

    /**
     * Watch product update in store state
     * when user clicks add to cart button on Product Card
     */
    stateProduct() {
      this.getSizesPerRow()
      this.getProductSizes()
      this.autoFocusFirstSize()
    },

    /**
     * Listen to the overlay changes
     */
    getActiveOverlay() {
      this.resetModalContent()
    },

    /**
     * Emit back in stock state
     */
    showBackInStockModal() {
      this.$emit('bis-active', this.showBackInStockModal)
    },

    /**
     * Watch showBackInStock prop to override local bis display
     */
    showBackInStock() {
      if (this.showBackInStock === this.showBackInStockModal) {
        return
      }

      this.showBackInStockModal = this.showBackInStock
    },
  },

  mounted() {
    this.getProductSizes()
    this.getSizesPerRow()
    this.autoFocusFirstSize()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setShowUpdatedSuccess: 'quickAddToCart/setUpdatedSuccess',
      addItem: 'cart/addItem',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      setLastAdded: 'quickAddToCart/setLastAdded',
      setProduct: 'quickAddToCart/setQuickAddProduct',
      setSize: 'quickAddToCart/setSize',
      setSizeGuideProduct: 'sizeGuideDrawer/setSizeGuideProduct',
      setShowUpsell: 'productFormDrawer/setShowOnSuccess',
    }),

    /**
     * Map product sizes data
     */
    getProductSizes() {
      if (!this.product && !this.stateProduct) {
        return
      }

      const product = this.product || this.stateProduct

      if (!product?.options?.length && !product?.options?.[1]) {
        return
      }

      const selectedVariant = product.selectedOrFirstAvailableVariant
      const selectedGroup = product.groupName || selectedVariant.selectedOptions[0]?.value
      this.sizes = []

      this.sizes = product.variants
        .map((variant) => {
          if (variant.selectedOptions[0]?.value?.toLowerCase() !== selectedGroup?.toLowerCase()) {
            return false
          }

          return {
            available: variant.available,
            id: variant.id,
            label: variant.selectedOptions[1]?.value,
            lowStock: variant.available && variant.inventory <= this.$variable('settings.lowStockThreshold'),
            preorder: product?.preorder?.skus?.includes(variant.sku),
            sku: variant.sku,
            value: variant.selectedOptions[1]?.value,
          }
        })
        .filter(Boolean)

      if (this.showBackInStockModal !== false) {
        this.showBackInStockModal = false
      }

      if (!['os', 'one size'].includes(this.sizes[0]?.label?.toLowerCase())) {
        this.isOneSize = false
        return
      }

      this.isOneSize = true
    },

    getStatusClass(size) {
      if (size.preorder) {
        return 'pre-order'
      }

      if (size.lowStock) {
        return 'low-stock'
      }

      if (!size.available) {
        return 'status-notify'
      }
      return ''
    },

    /**
     * Get sizes messages for mobile drawer
     * @param {Object} size
     */
    getMobileSizeMessage(size) {
      if (!size.available) {
        return this.$string('product.cta.notify_me')
      }

      if (size.preorder) {
        const product = this.product || this.stateProduct
        const date = this.$formatPreorderDate({
          date: product?.preorder?.date,
          dateFormat: 'preorder_pdp_html',
        })
        return this.$string('product.preorder.form', { replace: { date } })
      }

      if (size.lowStock) {
        return this.$string('product.cta.low_stock')
      }

      return ''
    },

    /**
     * Handle click on size
     * @param {Object} size - Clicked size.
     */
    handleClickOnSize(size) {
      this.selectedSize = size

      if (!this.selectedSize.available) {
        this.showBackInStockModal = true
        return
      }

      if (!this.showConfirmationButton) {
        this.addItemToCart()
      }
    },

    /**
     * Close quick add overlay.
     */
    closeQuickAdd() {
      this.closeOverlay('quick-add-to-cart-modal')
      this.removeItemAdded()
    },

    /**
     * Close mobile quick add success modal.
     */
    closeSuccessMobile() {
      this.closeOverlay('shop-the-look-success-modal')
      this.removeItemAdded()
    },

    /**
     * Remove item added after a delay.
     */
    async removeItemAdded() {
      await cnvs.Sleep(1000)
      this.itemAdded = false
    },

    /**
     * Reset content when modal closes
     */
    async resetModalContent() {
      if (
        this.getActiveOverlay?.namespace === 'quick-add-to-cart-modal' ||
        this.getActiveOverlay?.namespace === 'shop-the-look-success-modal'
      ) {
        return
      }

      await cnvs.Sleep(1000)
      this.itemAdded = false
    },

    /**
     * Add to cart handler
     */
    async addItemToCart() {
      if (!this.selectedSize.available || this.selectedSize.available === 'false') {
        return
      }

      clearTimeout(this.timeout)
      this.state = 'adding'

      if (cnvs.storybook) {
        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('slow'))
        return
      }

      this.clearCustomReportProps()

      const product = this.product || this.stateProduct
      this.lineItemProperties = {}
      this.addMetafieldsToProperties(product, this.selectedSize.sku)
      this.lineItemProperties = {
        ...this.lineItemProperties,
        ...this.getCustomReportProps(),
      }

      const items = [
        {
          id: this.selectedSize.id,
          properties: {
            ...this.lineItemProperties,
            ...this.properties,
          },
          quantity: 1,
        },
      ]

      this.setShowUpsellDesktop()

      try {
        await this.addItem(items)

        this.emitEventForWishlistPage()
        this.state = 'ready'
        this.itemAdded = true

        this.closeOverlay('product-form-drawer')
        this.displaySuccessModal()

      } catch (error) {
        this.state = 'ready'

        if (error?.description) {
          this.error = error.description
          return
        }

        cnvs.ReportError('Failed to quick add product to cart', error)
      }
    },

    /**
     * Add metafields to properties to retrieve them in cart.
     * @param {Object} product - Product data.
     * @param {Object} sku - Selected variant SKU.
     */
    addMetafieldsToProperties(product, sku) {
      const metafields = (this.product || this.liquid).metafields

      this.lineItemProperties = {
        ...this.lineItemProperties,
        metafields: {},
      }

      if (metafields.bottomBadge) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          bottomBadge: metafields.bottomBadge,
        }
      }

      if (metafields.discountCode) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          discountCode: metafields.discountCode,
        }
      }

      if (metafields.topBadge) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          topBadge: metafields.topBadge,
        }
      }

      if (metafields.specialtyBadge) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          specialtyBadge: metafields.specialtyBadge,
        }
      }

      if (metafields.matchingSet &&
        (metafields.matchingSet.product !== null ||
          metafields.matchingSet.description !== null ||
          metafields.matchingSet.button !== null)
      ) {
        this.lineItemProperties.metafields = {
          ...this.lineItemProperties.metafields,
          matchingSet: metafields.matchingSet,
        }
      }

      /**
       * Add preorder date.
       */
      if (!this.preorderLineItemProperties || !product?.preorder?.skus?.includes(sku)) {
        return
      }

      this.lineItemProperties.metafields = {
        ...this.lineItemProperties.metafields,
      }

      const date = this.$formatPreorderDate({
        date: product?.preorder?.date,
        dateFormat: 'preorder_checkout',
      })

      const preorderDate = this.$string('product.preorder.form', { replace: { date } })

      this.lineItemProperties = {
        ...this.lineItemProperties,
        Preorder: preorderDate,
      }
    },

    /**
     * Set product and display success modal.
     */
    async displaySuccessModal() {
      if (this.showCartDrawer && !this.isMobile) {
        this.openCartDrawer()

        return
      }

      const product = this.product || this.stateProduct

      if ((this.isStlType && !this.isMobile) || this.isPdpStl) {
        this.closeOverlay('shop-the-look-modal')
      }

      if ((this.isStlType || !this.isMobile) && !this.isPdpStl && !this.isWishlistPage) {
        this.setLastAdded(this.selectedSize.id)
      }

      this.setProduct({
        ...product,
        featuredImage: product.media?.[0]?.image || product.featuredImage,
      })

      this.setSize(this.selectedSize.value)

      if (this.placement === 'lookbook') {
        return
      }

      if (this.isMobile && this.showUpdatedSuccess) {
        const overlayNamespace = 'productFormSuccessMobile'
        this.itemAdded = false
        this.closeOverlay('quick-add-to-cart-modal')
        await cnvs.Sleep(this.$timing('normal'))

        this.openOverlay({
          ignoreDismissed: true,
          namespace: overlayNamespace,
        })

        this.setShowUpdatedSuccess(false)

        await cnvs.Sleep(1200)
        this.itemAdded = true
      }

      if (!this.isMobile || this.isPdpStl) {
        const overlayNamespace = this.isMobile
          ? 'shop-the-look-success-modal'
          : 'quick-add-success-modal'

        this.openOverlay({
          ignoreDismissed: true,
          namespace: overlayNamespace,
        })
      }

      if (this.isWishlistPage) {
        this.closeOverlay('wishlist-product-card')

        if (this.isMobile) {
          const overlayNamespace = 'wishlistSuccessMobile'

          this.openOverlay({
            ignoreDismissed: true,
            namespace: overlayNamespace,
          })
        }
      }
    },

    /**
     * Open cart drawer immidiately after upsell added
     */
    openCartDrawer() {
      window.cnvs?.EventBus?.emit('overlay:open-cart-drawer')
    },

    /**
     * Count of sizes to show in one row
     */
    getSizesPerRow() {
      if (this.sizes.length < 2) {
        this.sizeCountPerRow = 1
      } else if (this.sizes.length === 2 && this.sizes.every((size) => size.label.indexOf('-') > 0)) {
        this.sizeCountPerRow = 2
      } else if (this.sizes.length === 2 && this.sizes.some((size) => size.label.indexOf('-') > 0)) {
        this.sizeCountPerRow = this.numberOfSizesPerRow - 1
      } else if (this.numberOfSizesPerRow) {
        if (Shopify.country !== 'US' && Shopify.country !== 'CA' && this.sizes.every(size => !isNaN(size.label) && (/^[0-9]+(\.[0-9]+)?$/).test(size.label))) {
          this.sizeCountPerRow = this.numberOfSizesPerRow - 2
        } else {
          this.sizeCountPerRow = this.numberOfSizesPerRow
        }
      }
    },

    /**
     * Set focus on one size button
     */
    async autoFocusFirstSize() {
      await this.$nextTick()

      if (this.isStlType && this.sizes?.length === 1) {
        this.selectedSize = {
          available: this.sizes[0].available,
          id: this.sizes[0].id,
          value: this.sizes[0].value,
        }

        this.$refs.sizeButtons?.[0]?.[0]?.focus()
      } else {
        this.selectedSize = {}
      }
    },

    /**
     * Sets mobile vertical splide carousel reached top state value (Boolean)
     * This helps when the modal shelf on mobile is being closed by dragging
     *
     * If sizes splide carousel is not on top, we block drag-closing
     * If sizes splide carousel is on top, we activate drag-closing
     */
    setMobileVerticalScrollOnTop() {
      if (!this.isMobile || this.isMobileInline) {
        return
      }

      const splide = this.$refs.mobileSplideVertical

      splide.splide.event.off('visible')
      splide.splide.event.on('visible', (event) => {
        if (event.index === 0 && !this.mobileSizeScrollOnTop) {
          this.setMobileSizeScrollOnTop(true)
        }
      })

      splide.splide.event.off('hidden')
      splide.splide.event.on('hidden', (event) => {
        if (event.index === 0 && this.mobileSizeScrollOnTop) {
          this.setMobileSizeScrollOnTop(false)
        }
      })
    },

    /**
     * Open size guide drawer.
     */
    openSizeGuide() {
      this.setSizeGuideProduct(this.product)

      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'sizeGuideDrawer',
      })
    },

    /*
     * Emits event for wishlist button to remove item from the page
     */
    emitEventForWishlistPage() {
      if (this.isWishlistPage) {
        this.$emit('wishlist-add-to-cart')
      }
    },

    /**
     * Handle klaviyo form submit event.
     */
    handleKlaviyoFormSubmit() {
      this.$emit('form-submitted')
    },

    /**
     * Clear properties from custom reporting
     */
    clearCustomReportProps() {
      /* eslint-disable no-underscore-dangle */
      delete this.lineItemProperties._plpHotspot
      delete this.lineItemProperties._sectionHotspot
      delete this.lineItemProperties._pdpShopTheLook
      delete this.lineItemProperties._productTray
      delete this.lineItemProperties._addedFromWishlist
      delete this.lineItemProperties._makeItASet
      delete this.lineItemProperties._PDP_ATC_Path
      delete this.lineItemProperties._PLP_ATC_Path
      /* eslint-enable no-underscore-dangle */
    },

    /**
     * Properties for custom reporting
     */
    getCustomReportProps() {
      const pageSource = this.productCardLocation?.key
      const sku = this.selectedSize.sku || this.product?.selectedOrFirstAvailableVariant?.sku
      const pageUrl = window.location.pathname
      const metaobjectName = this.productCardLocation?.metaobjectName ||
        `${this.product.title} ${this.product.metafields?.swatches?.[0]?.name}`
      let customReportObject = {}

      if (this.isPLP) {
        // eslint-disable-next-line no-underscore-dangle
        customReportObject._PLP_ATC_Path = window.location.pathname
      }

      if (this.isPDP) {
        // eslint-disable-next-line no-underscore-dangle
        customReportObject._PDP_ATC_Path = window.location.pathname
      }

      if (pageSource === 'plpHotspot' ||
        pageSource === 'sectionHotspot' ||
        pageSource === 'pdpShopTheLook') {
        customReportObject[`_${pageSource}`] = `${sku}_${pageUrl}_${metaobjectName}`
      } else if (pageSource === 'productTray' ||
        pageSource === 'productTrayShopifyRecommendationsRelated' ||
        pageSource === 'productTrayShopifyRecommendationsComplemetary'
      ) {
        customReportObject[`_${pageSource}`] = `${sku}_${pageUrl}`
      } else if (pageSource === 'addedFromWishlist') {
        customReportObject[`_${pageSource}`] = sku
      } else if (pageSource === 'makeItASet_mini-cart' || pageSource === 'makeItASet_quickAddSuccess') {
        // eslint-disable-next-line no-underscore-dangle
        customReportObject[`_${pageSource}`] = this.productCardLocation?._upsell?.replace('#added_sku#', this.selectedSize.sku)
      }

      if (this.preorderVariant || this.preorderVariants) {
        const product = this.product || this.stateProduct
        const date = this.$formatPreorderDate({
          date: product?.preorder?.date,
          dateFormat: 'preorder_cart_html',
        })

        customReportObject = {
          ...customReportObject,
          _preOrderBadge: `Preorder: Ships after ${date}`,
          _preOrderDate: product?.preorder?.date,
        }
      }

      if (this.finalSale) {
        customReportObject = {
          ...customReportObject,
          _finalSale: `Final Sale: Product is final sale`,
        }
      }

      return customReportObject
    },

    /**
     * Check upsell eligible page for desktop atc
     */
    setShowUpsellDesktop() {
      if (this.productCardLocation?.key === 'productTray' ||
        this.productCardLocation?.key === 'carousel' ||
        this.productCardLocation?.key === 'productListing') {
        this.setShowUpsell(true)

        return
      }

      this.setShowUpsell(false)
    },

    /**
     * Sets history for tracking purposes in local storage of
     * card location and metaobject name
     */
    handleHrefClick() {
      const trackingHistory = {}
      if (this.productCardLocation?.key) {
        trackingHistory.pageSource = this.productCardLocation.key
      }

      if (this.productCardLocation?.metaobjectName) {
        trackingHistory.metaobjectName = this.productCardLocation.metaobjectName
      }

      localStorage.setItem('trackingHistory', JSON.stringify(trackingHistory))
    },

    /**
     * Updates option label
     */
    getOptionLabel(value) {
      if (Shopify.country !== 'US' && Shopify.country !== 'CA' && (/^[0-9]+(\.[0-9]+)?$/).test(value)) {
        return this.$string('product.cta.selected_value_non_us', { replace: { value } })
      }
      return value
    },

    /**
     * Get option display name
     */
    getOptionDisplayName(value) {
      if (Shopify.country !== 'US' && Shopify.country !== 'CA' && this.sizes.every(size => !isNaN(size.label) && (/^[0-9]+(\.[0-9]+)?$/).test(size.label))) {
        const sizeVal = value || ''
        return (/^[0-9]+(\.[0-9]+)?$/).test(value)
          ? this.$string('product.cta.selected_size_nonus', { replace: { size: `US ${sizeVal}` } })
          : this.$string('product.cta.selected_size_nonus', { replace: { size: `${sizeVal}` } })
      }

      return this.$string('product.cta.selected_size', { replace: { size: value || '' } })
    },

    /**
     * Set 'Select a size' label based on location
     */
    getSelectOptionText() {
      if (Shopify.country !== 'US' && this.sizes.every(size => !isNaN(size.label) && (/^[0-9]+(\.[0-9]+)?$/).test(size.label))) {
        return this.$string('product.cta.select_size_non_us')
      }

      return this.$string('product.cta.select_size')
    },

    /**
     * Checks if product ships to another country
     */
    isShippingUnavailable() {
      if (cnvs?.settings?.blockedProduct &&
        this.product.tags?.includes('international: block') &&
        Shopify.country !== 'US') {
        return true
      }

      return false
    },
  },
}
</script>

<style lang="scss">
@import './quick-add-to-cart';
</style>
